import React, { useState, useEffect, useCallback } from 'react';
import {
  ExternalLink, Check, X, SkipForward, Loader2, Inbox, RefreshCw, Sparkles, Clock, Copy, HelpCircle,
} from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { fetchWithCsrf } from '../../lib/fetchWithCsrf';
import {
  card, btnDone, btnGhost, input, categoryPill, platformPill, platformPillDefault,
  hitBadge, kfPass, kfFail, relativeTime, fullTimestamp,
} from './theme';

const FILTERS = [
  { key: 'unreviewed', label: 'Unreviewed' },
  { key: 'done', label: 'Done' },
  { key: 'skipped', label: 'Skipped' },
];

const SNIPPET_PREVIEW = 80;

/**
 * Signal Feed — compact, inbox-style list of Hunter's hit cards.
 * Hunter is listen-first: every row here is a structured cite-bait candidate
 * (buyer quote, why it matters, angle/title, keyword filter, overlap check,
 * FAQ line), not a reply to post. Engage is the rare exception -- its draft
 * only shows when the card actually earned an "Engage: yes" verdict.
 * Collapsed: one tight row per hit (platform, badges, angle/quote preview,
 * time, small Done/Skip). Click a row to expand the full card. Only one row
 * is expanded at a time.
 */
export default function SignalFeed({ onCountChange }) {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unreviewed');
  const [actingId, setActingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [counts, setCounts] = useState({ unreviewed: 0, done: 0, skipped: 0 });

  // Per-status counts (head-only, exact) — accurate even past the 200-row display cap.
  const fetchCounts = useCallback(async () => {
    const next = { unreviewed: 0, done: 0, skipped: 0 };
    await Promise.all(
      FILTERS.map(async (f) => {
        const { count } = await supabase
          .from('hunter_signals')
          .select('id', { count: 'exact', head: true })
          .eq('status', f.key);
        next[f.key] = count || 0;
      }),
    );
    setCounts(next);
  }, []);

  const fetchSignals = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hunter_signals')
      .select('*')
      .eq('status', filter)
      .order('found_at', { ascending: false })
      .limit(200);
    if (!error && data) setSignals(data);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchSignals();
    fetchCounts();
    setExpandedId(null);
  }, [fetchSignals, fetchCounts]);

  const resolve = async (id, newStatus) => {
    setActingId(id);
    const { error } = await supabase
      .from('hunter_signals')
      .update({ status: newStatus, reviewed_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setSignals((prev) => prev.filter((s) => s.id !== id));
      if (expandedId === id) setExpandedId(null);
      setCounts((prev) => ({
        ...prev,
        [filter]: Math.max(0, prev[filter] - 1),
        [newStatus]: (prev[newStatus] || 0) + 1,
      }));
      onCountChange && onCountChange();
    }
    setActingId(null);
  };

  return (
    <div>
      {/* Filter row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => {
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {f.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {counts[f.key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {counts.unreviewed + counts.done + counts.skipped} total
          </span>
          <button
            onClick={() => { fetchSignals(); fetchCounts(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : signals.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Inbox className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-700">
            {filter === 'unreviewed' ? 'No signals to review' : `No ${filter} signals`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            New flagged posts will appear here once the hunter is running.
          </p>
        </div>
      ) : (
        <div className={`${card} divide-y divide-gray-100 overflow-hidden`}>
          {signals.map((s) => (
            <SignalRow
              key={s.id}
              signal={s}
              expanded={expandedId === s.id}
              onToggle={() => setExpandedId((cur) => (cur === s.id ? null : s.id))}
              acting={actingId === s.id}
              onResolve={resolve}
              showActions={filter === 'unreviewed'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformBadge({ platform }) {
  const key = (platform || '').toLowerCase();
  return (
    <span className={`px-1.5 py-0.5 text-[11px] font-semibold rounded capitalize ${platformPill[key] || platformPillDefault}`}>
      {platform}
    </span>
  );
}

function SignalRow({ signal, expanded, onToggle, acting, onResolve, showActions }) {
  const [draft, setDraft] = useState(signal.suggested_response || '');
  const [copied, setCopied] = useState(false);

  // Re-sync the editable draft if the row's suggested_response changes.
  useEffect(() => {
    setDraft(signal.suggested_response || '');
  }, [signal.suggested_response]);

  const [regenerating, setRegenerating] = useState(false);

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* clipboard blocked; no-op */
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await fetchWithCsrf(`/api/admin/hunter/signals/${signal.id}/regenerate`, { method: 'POST' });
      if (res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.suggested_response) setDraft(body.suggested_response);
      }
    } catch (_) {
      /* leave the existing draft in place on failure */
    } finally {
      setRegenerating(false);
    }
  };

  // Collapsed preview: the cite-bait angle if the card has one (more useful
  // than a raw snippet), falling back to the buyer quote, then the raw snippet
  // for any pre-rebuild legacy row that has neither.
  const previewText = signal.angle_title || signal.buyer_quote || signal.post_snippet || '';
  const preview = previewText.slice(0, SNIPPET_PREVIEW);
  const truncated = previewText.length > SNIPPET_PREVIEW;

  return (
    <div className={expanded ? 'bg-gray-50/60' : 'hover:bg-gray-50 transition-colors'}>
      {/* Collapsed row */}
      <div
        onClick={onToggle}
        className="flex items-center gap-3 px-3 py-2 cursor-pointer select-none"
      >
        <PlatformBadge platform={signal.platform} />
        {signal.category === 'Competitor' && (
          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded whitespace-nowrap ${categoryPill.Competitor}`}>
            Competitor
          </span>
        )}
        {signal.engage && (
          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded whitespace-nowrap ${hitBadge.engage}`}>
            Engage
          </span>
        )}
        {signal.overlap_status === 'duplicate' && (
          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded whitespace-nowrap ${hitBadge.duplicate}`}>
            Duplicate
          </span>
        )}
        <span className="flex-1 min-w-0 truncate text-sm text-gray-700">
          {preview}{truncated ? '…' : ''}
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap" title={fullTimestamp(signal.found_at)}>
          <Clock className="w-3 h-3" />
          {relativeTime(signal.found_at)}
        </span>
        {showActions && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              title="Done"
              disabled={acting}
              onClick={() => onResolve(signal.id, 'done')}
              className="p-1.5 rounded-md text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              title="Skip"
              disabled={acting}
              onClick={() => onResolve(signal.id, 'skipped')}
              className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Expanded detail -- the full hit card. Every value gets an explicit
          uppercase caption (Field below) instead of relying on formatting
          (italics/bold/icons) to signal what it is. */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-3">
          {/* Source link */}
          {signal.source_url && (
            <Field label="Source">
              <a
                href={signal.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 break-all"
              >
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                {signal.source_url}
              </a>
            </Field>
          )}

          {/* Buyer quote */}
          {signal.buyer_quote && (
            <Field label="What they actually wrote">
              <blockquote className="border-l-2 border-indigo-200 pl-3 text-sm text-gray-800 italic leading-relaxed">
                "{signal.buyer_quote}"
              </blockquote>
            </Field>
          )}

          {/* Angle / working title */}
          {signal.angle_title && (
            <Field label="Cite-bait angle (working title for a blog post/FAQ)">
              <p className="text-sm text-gray-800">{signal.angle_title}</p>
            </Field>
          )}

          {/* Why it matters */}
          {signal.why_it_matters && (
            <Field label="Why this matters for SurFox AI">
              <p className="text-sm text-gray-700">{signal.why_it_matters}</p>
            </Field>
          )}

          {/* Legacy fallback: pre-rebuild rows have a snippet but none of the fields above */}
          {!signal.buyer_quote && !signal.angle_title && signal.post_snippet && (
            <Field label="Legacy signal (from before the 2026-09-20 rebuild)">
              <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-wrap">{signal.post_snippet}</p>
            </Field>
          )}

          {/* Keyword filter */}
          {(signal.kf_wants_this !== null && signal.kf_wants_this !== undefined) && (
            <Field label="Keyword filter (does this hold up as real cite-bait?)">
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <KfCheck ok={signal.kf_wants_this} label="People actually search this" />
                <KfCheck ok={signal.kf_in_category} label="Stays in our category" />
                <KfCheck ok={signal.kf_path_to_surfox} label="Real path to SurFox AI" />
              </div>
            </Field>
          )}

          {/* Overlap check */}
          {signal.overlap_status && (
            <Field label="Overlap with a post you've already shipped">
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded ${signal.overlap_status === 'duplicate' ? hitBadge.duplicate : hitBadge.net_new}`}>
                {signal.overlap_status === 'duplicate' ? (
                  <>Duplicate of{' '}
                    <a href={signal.overlap_url} target="_blank" rel="noopener noreferrer" className="underline break-all">
                      {signal.overlap_url}
                    </a>
                  </>
                ) : 'Net new — nothing shipped covers this yet'}
              </div>
            </Field>
          )}

          {/* FAQ line */}
          {signal.faq_line && (
            <Field label="FAQ line (how a buyer would ask an AI search engine this)">
              <div className="flex items-start gap-1.5 text-sm text-gray-700">
                <HelpCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
                <span>{signal.faq_line}</span>
              </div>
            </Field>
          )}

          {/* Engage draft -- ONLY when this card earned an engage verdict. Rare,
              manual-approve; never a reply queue. */}
          <Field label="Engage this person? (rare — only when they're clearly shopping the category)">
            <span className={`text-sm font-semibold ${signal.engage ? 'text-green-700' : 'text-gray-400'}`}>
              {signal.engage ? 'Yes' : 'No'}
            </span>
          </Field>
          {signal.engage && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700">
                  <Sparkles className="w-3.5 h-3.5" />
                  Founder-voice draft (copy manually if you want to post it)
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={regenerate}
                    disabled={regenerating}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? 'Regenerating' : 'Regenerate'}
                  </button>
                  <button
                    onClick={copyDraft}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                className={`${input} w-full resize-y leading-relaxed`}
              />
            </div>
          )}

          {/* Full-size actions */}
          {showActions && (
            <div className="flex items-center gap-2 pt-1">
              <button className={btnDone} disabled={acting} onClick={() => onResolve(signal.id, 'done')}>
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Done
              </button>
              <button className={btnGhost} disabled={acting} onClick={() => onResolve(signal.id, 'skipped')}>
                <SkipForward className="w-4 h-4" />
                Skip
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Explicit caption + value, so every field on the card says what it is
// instead of relying on italics/bold/icons to imply it.
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

function KfCheck({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      {ok ? <Check className={`w-3.5 h-3.5 ${kfPass}`} /> : <X className={`w-3.5 h-3.5 ${kfFail}`} />}
      {label}
    </span>
  );
}
